import { goAnchor } from "./app-nav";
import { locale, t, tn } from "@/lib/i18n";
import { useEffect, useMemo, useRef, useState } from "react";
import { Check } from "lucide-react";
import { usePage, type SectionProps } from "./context";
import { Band, Head, cn, rv, str } from "./ui";
import {
  contactTabs,
  dashboardHints,
  dashboardMeta,
  dashboardTeam,
  demoContacts,
  lifecycleCards,
  statusFilters,
} from "@/mocks/dashboard";

/* Живой макет окна «Дашборд»: полоса пробного периода, пустое состояние каналов,
   карточки жизненного цикла, контакты и нагрузка команды. Переключатель «Демо-данные»
   оживляет пустое рабочее пространство — счётчики, контакты и участники наполняются. */

const railItems = [
  { id: "dashboard", icon: "ri-dashboard-line", label: t("Дашборд"), anchor: "#dashboard" },
  { id: "inbox", icon: "ri-inbox-line", label: t("Входящие"), anchor: "#inbox-app" },
  { id: "contacts", icon: "ri-contacts-book-2-line", label: t("Контакты"), anchor: "#contacts-app" },
  { id: "channels", icon: "ri-share-forward-line", label: t("Каналы"), anchor: "#channels" },
  { id: "ai-assist", icon: "ri-sparkling-line", label: "AI Assist", anchor: "#ai-assist" },
  { id: "ai-agents", icon: "ri-robot-2-line", label: t("AI-агенты"), anchor: "#ai-agents" },
  { id: "broadcasts", icon: "ri-mail-send-line", label: t("Рассылки"), anchor: "#broadcasts" },
  { id: "workflows", icon: "ri-flow-chart", label: t("Рабочие процессы"), anchor: "#workflows" },
  { id: "reports", icon: "ri-bar-chart-2-line", label: t("Отчеты"), anchor: "#reports" },
  { id: "settings", icon: "ri-settings-3-line", label: t("Настройки"), anchor: "#settings" },
  { id: "lifecycle", icon: "ri-refresh-line", label: t("Жизненный цикл"), anchor: "#lifecycle" },
];

function DashboardDemo({ title, subtitle }: SectionProps) {
  const { brand } = usePage();
  const [demo, setDemo] = useState(false);
  const [tab, setTab] = useState("open");
  const [status, setStatus] = useState("all");
  const [stage, setStage] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [updated, setUpdated] = useState(t("только что"));
  const [toast, setToast] = useState("");
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const clock = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(
    () => () => {
      clearTimeout(timer.current);
      clearTimeout(clock.current);
    },
    [],
  );

  const flash = (text: string) => {
    setToast(text);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setToast(""), 4600);
  };

  const go = (anchor: string, label: string) => {
    if (goAnchor(anchor)) return;
    flash(t("Раздел «{label}» в демо не открывается", { label }));
  };

  const refresh = () => {
    if (refreshing) return;
    setRefreshing(true);
    clock.current = setTimeout(() => {
      setRefreshing(false);
      const time = new Date().toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
      setUpdated(t("сегодня в {time}", { time }));
      flash(t("Данные обновлены — счётчики пересчитаны"));
    }, 850);
  };

  const counts = useMemo(
    () => ({
      open: demo ? demoContacts.length : 0,
      assigned: demo ? demoContacts.filter((c) => c.assignee).length : 0,
      unassigned: demo ? demoContacts.filter((c) => !c.assignee).length : 0,
    }),
    [demo],
  );

  const contacts = useMemo(() => {
    if (!demo) return [];
    let list = demoContacts;
    if (tab === "assigned") list = list.filter((c) => c.assignee);
    if (tab === "unassigned") list = list.filter((c) => !c.assignee);
    if (stage) list = list.filter((c) => c.stage === lifecycleCards.find((s) => s.id === stage)?.name);
    return list;
  }, [demo, tab, stage]);

  const members = useMemo(() => {
    if (!demo) return dashboardTeam.slice(0, 1);
    if (status === "online") return dashboardTeam.filter((m) => m.online);
    if (status === "offline") return dashboardTeam.filter((m) => !m.online);
    return dashboardTeam;
  }, [demo, status]);

  const stageName = lifecycleCards.find((s) => s.id === stage)?.name ?? "";

  const toggleDemo = () => {
    const next = !demo;
    setDemo(next);
    setStage(null);
    flash(next ? t("Демо-данные включены: 35 контактов на четырёх этапах") : t("Демо-данные выключены — снова пустое рабочее пространство"));
  };

  return (
    <Band id="dashboard" tone="default">
      <Head title={title} subtitle={subtitle} />
      <div {...rv()} className="flash-card relative overflow-hidden rounded-card border border-border bg-card !p-0">
        {/* полоска «окна приложения» */}
        <div className="flex flex-wrap items-center gap-2.5 border-b border-border px-4 py-3">
          <span className="flex gap-1.5" aria-hidden>
            <span className="size-2.5 rounded-full bg-foreground/20" />
            <span className="size-2.5 rounded-full bg-foreground/20" />
            <span className="size-2.5 rounded-full bg-foreground/20" />
          </span>
          <span className="flash-mono ms-1 truncate text-[11px] text-muted-foreground">
            {str(brand) || "Hub"} / {t("рабочее пространство → дашборд")}
          </span>
          <div className="ms-auto flex flex-wrap items-center gap-2">
            <span className="whitespace-nowrap text-[10.5px] text-muted-foreground">{t("Обновлено")} {updated}</span>
            <button
              type="button"
              onClick={refresh}
              className="inline-flex h-7 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-btn border border-border px-2.5 text-[11px] font-semibold transition hover:border-primary/50 hover:text-primary"
            >
              <i className={cn("ri-refresh-line text-[12px] leading-none", refreshing && "animate-spin")} aria-hidden />
              {refreshing ? t("Обновляем") : t("Обновить")}
            </button>
          </div>
        </div>

        {/* пробный период */}
        <div className="flex flex-wrap items-center gap-2 border-b border-border bg-primary/[.08] px-3.5 py-2 text-[11.5px]">
          <i className="ri-information-line text-[13px] leading-none text-primary" aria-hidden />
          <span className="min-w-0 flex-1 leading-relaxed text-foreground/80">
            {t("Пробный период {plan} закончится {ends} — через {days} дней. Обновите тариф, чтобы не потерять доступ.", {
              plan: dashboardMeta.plan,
              ends: dashboardMeta.trialEnds,
              days: dashboardMeta.trialDays,
            })}
          </span>
          <button
            type="button"
            onClick={() => go("#onboarding", t("Начало работы"))}
            className="inline-flex h-6 flex-none cursor-pointer items-center whitespace-nowrap rounded-full border border-primary/30 px-2.5 text-[10.5px] font-semibold text-primary transition hover:bg-primary/10"
          >
            {t("Чеклист онбординга")}
          </button>
          <button
            type="button"
            onClick={() => flash(t("Демо-режим: смена тарифа здесь недоступна"))}
            className="inline-flex h-6 flex-none cursor-pointer items-center whitespace-nowrap rounded-full bg-primary px-2.5 text-[10.5px] font-semibold text-primary-foreground transition hover:brightness-110"
          >
            {t("Обновить тариф")}
          </button>
        </div>

        <div className="flex">
          {/* иконная панель как в приложении */}
          <div className="hidden w-12 flex-none flex-col items-center gap-1 border-e border-border py-3 lg:flex">
            {railItems.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => go(r.anchor, r.label)}
                title={r.label}
                aria-label={r.label}
                className={cn(
                  "grid size-8 cursor-pointer place-items-center rounded-btn transition",
                  r.id === "dashboard" ? "bg-primary/12 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground",
                )}
              >
                <i className={cn(r.icon, "text-[15px] leading-none")} aria-hidden />
              </button>
            ))}
            <span className="mt-auto grid size-8 place-items-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground" aria-hidden>
              {t("В")}
            </span>
          </div>

          <div className="min-w-0 flex-1 p-3.5 md:p-5">
            {/* заголовок рабочей области */}
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <h3 className="text-base font-semibold leading-snug">{t("Дашборд")}</h3>
                <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-muted-foreground">
                  <span className="inline-flex items-center gap-1">
                    <i className="ri-time-line text-[12px] leading-none" aria-hidden />
                    {t("Часовой пояс — {tz}", { tz: dashboardMeta.timeZone })}
                  </span>
                  <span className="text-border" aria-hidden>
                    ·
                  </span>
                  <span>{tn(demo ? 35 : 0, "контакт", "контакта", "контактов")}</span>
                </p>
              </div>

              <div className="flex items-center gap-2">
                <span className="whitespace-nowrap text-[10.5px] font-medium text-muted-foreground">{t("Демо-данные")}</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={demo}
                  onClick={toggleDemo}
                  className={cn(
                    "relative h-5 w-9 flex-none cursor-pointer rounded-full border transition",
                    demo ? "border-primary bg-primary" : "border-border bg-foreground/10",
                  )}
                >
                  <span
                    className={cn(
                      "absolute top-1/2 size-3.5 -translate-y-1/2 rounded-full bg-card transition-all",
                      demo ? "start-[1.15rem]" : "start-[0.15rem]",
                    )}
                    aria-hidden
                  />
                </button>
              </div>
            </div>

            {/* пустое состояние каналов */}
            <div className="mt-3.5 grid overflow-hidden rounded-btn border border-border md:grid-cols-[1.15fr_.85fr]">
              <div className="flex flex-col items-start gap-2.5 bg-primary/[.06] p-4 md:p-5">
                <h4 className="flex items-center gap-2 text-[13.5px] font-semibold leading-snug">
                  <i className="ri-plug-line text-[15px] leading-none text-primary" aria-hidden />
                  {demo ? t("Каналы ещё не подключены") : t("У вас нет подключённых каналов")}
                </h4>
                <p className="max-w-sm text-[11.5px] leading-relaxed text-muted-foreground">
                  {t("Можно подключить все аккаунты переписки в одном месте — это и есть каналы. Подключите канал, чтобы диалоги попали во входящие и дашборд начал считать контакты.")}
                </p>
                <button
                  type="button"
                  onClick={() => go("#channels", t("Каналы"))}
                  className="mt-1 inline-flex h-8 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-btn bg-primary px-3.5 text-[12px] font-semibold text-primary-foreground transition hover:brightness-110"
                >
                  <i className="ri-add-line text-[13px] leading-none" aria-hidden />
                  {t("Подключить канал")}
                </button>
              </div>

              <div
                className="relative hidden overflow-hidden p-3 md:block"
                style={{
                  backgroundImage:
                    "linear-gradient(140deg, color-mix(in srgb, var(--f-deep) 92%, #000), color-mix(in srgb, var(--f-primary) 42%, #0b1020))",
                }}
              >
                <div className="absolute inset-0 opacity-30" aria-hidden>
                  <div
                    className="absolute inset-0"
                    style={{ backgroundImage: "radial-gradient(60% 70% at 20% 10%, rgba(255,255,255,.28), transparent 70%)" }}
                  />
                </div>
                <div className="relative flex h-full items-center justify-center">
                  <div className="w-full max-w-[15rem] rounded-btn border border-white/15 bg-white/10 p-2 backdrop-blur">
                    <div className="flex gap-1" aria-hidden>
                      <span className="size-1.5 rounded-full bg-white/40" />
                      <span className="size-1.5 rounded-full bg-white/40" />
                      <span className="size-1.5 rounded-full bg-white/40" />
                    </div>
                    <div className="mt-1.5 grid gap-1">
                      {[
                        { icon: "ri-whatsapp-line", name: t("Анна Ковалёва"), text: t("Здравствуйте! Подскажите цену") },
                        { icon: "ri-telegram-line", name: t("Игорь Дёмин"), text: t("Счёт получил, оплачу завтра") },
                        { icon: "ri-mail-line", name: t("Марина Гусева"), text: t("Нужна консультация по тарифу") },
                      ].map((m) => (
                        <div key={m.name} className="flex items-center gap-1.5 rounded-[5px] bg-white/10 px-1.5 py-1">
                          <i className={cn(m.icon, "text-[11px] leading-none text-white/80")} aria-hidden />
                          <div className="min-w-0">
                            <p className="truncate text-[8.5px] font-semibold text-white">{m.name}</p>
                            <p className="truncate text-[8px] text-white/70">{m.text}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* жизненный цикл */}
            <div className="mt-4 flex items-center justify-between gap-2">
              <h4 className="flex items-center gap-1.5 text-[12.5px] font-semibold">
                {t("Жизненный цикл")}
                {stageName ? (
                  <button
                    type="button"
                    onClick={() => setStage(null)}
                    className="inline-flex cursor-pointer items-center gap-1 rounded-full bg-primary/12 px-1.5 py-0.5 text-[9.5px] font-semibold text-primary"
                  >
                    {stageName}
                    <i className="ri-close-line text-[10px] leading-none" aria-hidden />
                  </button>
                ) : null}
              </h4>
              <button
                type="button"
                onClick={() => go("#lifecycle", t("Жизненный цикл"))}
                aria-label={t("Настроить жизненный цикл")}
                title={t("Настроить жизненный цикл")}
                className="grid size-7 cursor-pointer place-items-center rounded-btn text-muted-foreground transition hover:bg-muted hover:text-primary"
              >
                <i className="ri-settings-3-line text-[14px] leading-none" aria-hidden />
              </button>
            </div>

            <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
              {lifecycleCards.map((s) => {
                const active = stage === s.id;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setStage(active ? null : s.id)}
                    aria-pressed={active}
                    className={cn(
                      "flex cursor-pointer flex-col gap-2 rounded-btn border p-3 text-start transition",
                      active ? "border-primary/50 bg-primary/[.06]" : "border-border bg-background/40 hover:border-primary/40",
                    )}
                  >
                    <span className="flex items-center gap-1.5 text-[11.5px] font-semibold leading-snug">
                      <i className={cn(s.icon, "text-[13px] leading-none text-primary")} aria-hidden />
                      <span className="truncate">{s.name}</span>
                    </span>
                    <span className="flex items-baseline gap-2">
                      <b className="text-[19px] font-semibold leading-none tabular-nums">{demo ? s.demoCount : 0}</b>
                      <span className="text-[10.5px] tabular-nums text-muted-foreground">{demo ? s.demoShare : s.share}</span>
                    </span>
                    <span className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-border/70 pt-2">
                      {s.metrics.map((m) => (
                        <span key={m.label} title={m.label} className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                          <i className={cn(m.icon, "text-[11px] leading-none")} aria-hidden />
                          <span className="tabular-nums">{demo ? m.demoValue : m.value}</span>
                        </span>
                      ))}
                    </span>
                  </button>
                );
              })}
            </div>

            <p className="mt-1.5 text-[10.5px] text-muted-foreground">
              {t("Нажмите на этап, чтобы отфильтровать контакты ниже.")}
            </p>

            {/* контакты + команда */}
            <div className="mt-4 grid gap-2.5 lg:grid-cols-2">
              <div className="flex min-h-[15rem] flex-col rounded-btn border border-border bg-background/40">
                <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
                  <h4 className="text-[12.5px] font-semibold">{t("Контакты")}</h4>
                  <button
                    type="button"
                    onClick={() => go("#fields", t("Поля контактов"))}
                    aria-label={t("Настроить поля контактов")}
                    title={t("Настроить поля контактов")}
                    className="grid size-6 cursor-pointer place-items-center rounded-btn text-muted-foreground transition hover:bg-muted hover:text-primary"
                  >
                    <i className="ri-settings-3-line text-[13px] leading-none" aria-hidden />
                  </button>
                </div>

                <div className="flex gap-1 border-b border-border px-2.5 py-1.5">
                  {contactTabs.map((tabItem) => {
                    const value = counts[tabItem.id as keyof typeof counts];
                    const active = tab === tabItem.id;
                    return (
                      <button
                        key={tabItem.id}
                        type="button"
                        onClick={() => setTab(tabItem.id)}
                        aria-pressed={active}
                        className={cn(
                          "inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-btn px-2 py-1 text-[11.5px] font-medium transition",
                          active ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground",
                        )}
                      >
                        {tabItem.label}
                        <span className={cn("tabular-nums", active ? "text-primary" : "text-muted-foreground/80")}>{value}</span>
                      </button>
                    );
                  })}
                </div>

                {contacts.length === 0 ? (
                  <div className="flex flex-1 flex-col items-center justify-center gap-1.5 px-4 py-6 text-center">
                    <span className="grid size-11 place-items-center rounded-full border border-dashed border-border text-muted-foreground/70" aria-hidden>
                      <i className="ri-inbox-line text-[18px] leading-none" />
                    </span>
                    <p className="text-[11.5px] font-semibold">
                      {!demo ? t("Пока нет контактов") : stageName ? t("На этапе «{stage}» пусто", { stage: stageName }) : t("Здесь пока пусто")}
                    </p>
                    <p className="max-w-[15rem] text-[10.5px] leading-relaxed text-muted-foreground">
                      {!demo
                        ? t("Подключите канал — и диалоги клиентов появятся во входящих и на дашборде.")
                        : stageName
                          ? t("Снимите фильтр по этапу или выберите другую вкладку.")
                          : t("В этой вкладке нет контактов — попробуйте другую.")}
                    </p>
                    {!demo ? (
                      <button
                        type="button"
                        onClick={() => go("#channels", t("Каналы"))}
                        className="mt-1 cursor-pointer text-[10.5px] font-semibold text-primary underline decoration-dotted"
                      >
                        {t("Подключить канал")}
                      </button>
                    ) : null}
                  </div>
                ) : (
                  <ul className="flex flex-col divide-y divide-border/70">
                    {contacts.map((c) => (
                      <li key={c.id} className="flex items-center gap-2 px-3 py-1.5">
                        <span className="grid size-6 flex-none place-items-center rounded-full bg-primary/12 text-[9px] font-bold text-primary" aria-hidden>
                          {c.initials}
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-[11.5px] font-semibold leading-snug">{c.name}</p>
                          <p className="flex items-center gap-1 truncate text-[10px] text-muted-foreground">
                            <i className={cn(c.channelIcon, "text-[10px] leading-none")} aria-hidden />
                            {c.channel} · {c.assignee || t("не назначен")}
                          </p>
                        </div>
                        <span className="hidden whitespace-nowrap rounded-full bg-muted px-1.5 py-0.5 text-[9px] font-semibold text-foreground/70 sm:inline">
                          {c.stage}
                        </span>
                        {c.unread > 0 ? (
                          <span className="grid size-4 flex-none place-items-center rounded-full bg-primary text-[8.5px] font-bold text-primary-foreground" aria-hidden>
                            {c.unread}
                          </span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="flex min-h-[15rem] flex-col rounded-btn border border-border bg-background/40">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-3 py-2">
                  <h4 className="text-[12.5px] font-semibold">{t("Участники команды")}</h4>
                  <div className="relative">
                    <select
                      value={status}
                      onChange={(e) => {
                        setStatus(e.target.value);
                        flash(t("Фильтр участников: {label}", { label: statusFilters.find((s) => s.id === e.target.value)?.label ?? "" }));
                      }}
                      aria-label={t("Фильтр участников по статусу")}
                      className="h-7 w-[8.5rem] cursor-pointer appearance-none rounded-btn border border-border bg-background pe-6 ps-2 text-[11px] focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                    >
                      {statusFilters.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.label}
                        </option>
                      ))}
                    </select>
                    <i className="ri-arrow-down-s-line pointer-events-none absolute end-1 top-1/2 -translate-y-1/2 text-[12px] leading-none text-muted-foreground" aria-hidden />
                  </div>
                </div>

                {members.length === 0 ? (
                  <div className="flex flex-1 flex-col items-center justify-center gap-1.5 px-4 py-6 text-center">
                    <span className="grid size-11 place-items-center rounded-full border border-dashed border-border text-muted-foreground/70" aria-hidden>
                      <i className="ri-team-line text-[18px] leading-none" />
                    </span>
                    <p className="text-[11.5px] font-semibold">{t("Никого нет по этому статусу")}</p>
                    <button
                      type="button"
                      onClick={() => setStatus("all")}
                      className="mt-0.5 cursor-pointer text-[10.5px] font-semibold text-primary underline decoration-dotted"
                    >
                      {t("Показать всех")}
                    </button>
                  </div>
                ) : (
                  <ul className="flex flex-col divide-y divide-border/70">
                    {members.map((m) => (
                      <li key={m.id} className="flex items-center gap-2 px-3 py-2">
                        <span className="relative grid size-7 flex-none place-items-center rounded-full bg-primary/12 text-[9.5px] font-bold text-primary" aria-hidden>
                          {m.initials}
                          <span
                            className={cn(
                              "absolute -end-0.5 -bottom-0.5 size-2 rounded-full border-2 border-card",
                              m.online ? "bg-emerald-500" : "bg-foreground/30",
                            )}
                          />
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-[11.5px] font-semibold leading-snug">{m.name}</p>
                          <p className="truncate text-[10px] text-muted-foreground">
                            {m.role} · {t("назначено")} {tn(demo ? m.demoContacts : 0, "контакт", "контакта", "контактов")}
                          </p>
                        </div>
                        <span className="hidden whitespace-nowrap text-[10px] text-muted-foreground sm:inline">{m.since}</span>
                      </li>
                    ))}
                  </ul>
                )}

                <div className="mt-auto flex items-center justify-between gap-2 border-t border-border px-3 py-1.5">
                  <span className="text-[10px] text-muted-foreground">
                    {demo ? t("{n} из {total} участников", { n: members.length, total: dashboardTeam.length }) : t("1 участник")}
                  </span>
                  <button
                    type="button"
                    onClick={() => go("#team-settings", t("Настройки команды"))}
                    className="cursor-pointer text-[10px] font-semibold text-primary underline decoration-dotted"
                  >
                    {t("Настройки команды")}
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-4 grid gap-2 border-t border-border/60 pt-3 sm:grid-cols-3">
              {dashboardHints.map((h) => (
                <div key={h.title} className="rounded-btn border border-border bg-background/40 p-2.5">
                  <p className="flex items-center gap-1.5 text-[11.5px] font-semibold">
                    <i className={cn(h.icon, "text-[13px] leading-none text-primary")} aria-hidden />
                    {h.title}
                  </p>
                  <p className="mt-1 text-[10.5px] leading-relaxed text-muted-foreground">{h.text}</p>
                </div>
              ))}
            </div>

            <p className="mt-3 text-[11px] text-muted-foreground">
              {t("Демо-режим: цифры и списки меняются только на этой странице и не сохраняются после перезагрузки.")}
            </p>
          </div>
        </div>

        {toast ? (
          <div
            role="status"
            className="pointer-events-none absolute inset-x-3 bottom-3 z-40 mx-auto flex max-w-md items-start gap-2 rounded-btn border border-primary/40 bg-background/95 px-3.5 py-2.5 text-[12px] text-foreground shadow-lift backdrop-blur"
          >
            <Check className="mt-0.5 size-3.5 flex-none text-primary" strokeWidth={3} aria-hidden />
            {toast}
          </div>
        ) : null}
      </div>
    </Band>
  );
}

export default DashboardDemo;